import { AutoComplete, Button, Form, Input, message } from 'antd';
import { IconCode, IconPlus, IconTrash } from '@tabler/icons-react';
import CModal from '../CModal';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { apiV1 } from '../../utils/apiClient';
import { Customer } from '@next-is-free-monorepo/next-is-free-types';
import { customerQueryOptions } from '../../utils/queryOptions';

interface CustomerMetadataModalProps {
  open: boolean;
  onClose: () => void;
  customer: Customer;
}

const CustomerMetadataModal = ({ open, onClose, customer }: CustomerMetadataModalProps) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const metadata = useSuspenseQuery({
    queryKey: ['customers-metadata-keys'], queryFn: async () => {
      const metadata = await apiV1.get<{key: string}[]>('/shop/customers/metadata')
      return metadata.map(item => ({value: item.key}))
    }
  });


  const updateCustomerMetadata = useMutation({
    mutationFn: async (metadataObj: Record<string, string>) => {
      await apiV1.put(`/shop/customers/${customer.id}`, { metadata: metadataObj });
    }
  });

  const handleFinish = (values: { metadata: { key: string; value: string }[] }) => {
    const keys = values.metadata.map((item) => item.key.trim());

    const hasDuplicates = new Set(keys).size !== keys.length;
    if (hasDuplicates) {
      message.error('Le chiavi devono essere uniche');
      return;
    }

    const metadataObj = Object.fromEntries(
      values.metadata.map(({ key, value }) => [key.trim(), value])
    );

    updateCustomerMetadata.mutate(metadataObj, {
      onSuccess: () => {
        message.success('Campi aggiornati con successo');
        onClose();
        queryClient.invalidateQueries({ queryKey: customerQueryOptions(customer.id).queryKey });
      }
    });
  };

  return (
    <CModal
      mutationOptions={{}}
      title="Modifica campi personalizzati"
      width={600}
      open={open}
      onClose={onClose}
      icon={<IconCode />}
      customFooter={
        <div className="flex items-center justify-between">
          <Button
            type="text"
            icon={<IconPlus size={16} />}
            onClick={() => {
              const list = form.getFieldValue('metadata') || [];
              form.setFieldsValue({ metadata: [...list, { key: '', value: '' }] });
            }}
          >
            Nuovo Campo
          </Button>
          <div className="flex gap-2">
            <Button onClick={onClose}>Annulla</Button>
            <Button type="primary" onClick={() => form.submit()}>
              Salva
            </Button>
          </div>
        </div>
      }
    >
      <Form
        className="mt-5"
        form={form}
        onFinish={handleFinish}
        initialValues={{
          metadata: customer.metadata
            ? Object.entries(customer.metadata).map(([key, value]) => ({ key, value }))
            : [{ key: '', value: '' }]
        }}
        layout="vertical"
      >
        <Form.List name="metadata">
          {(fields, { remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div key={key} className="flex gap-2 mb-2 items-start">
                  <Form.Item
                    {...restField}
                    name={[name, 'key']}
                    rules={[
                      { required: true, message: 'Inserisci una chiave' },
                      {
                        validator(_, value) {
                          if (!value) return Promise.resolve();
                          const allKeys = (form.getFieldValue('metadata') || []).map(
                            (item: any) => item?.key?.trim()
                          );
                          const duplicates = allKeys.filter((k: any) => k === value?.trim()).length;
                          return duplicates > 1
                            ? Promise.reject('Le chiavi devono essere uniche')
                            : Promise.resolve();
                        }
                      }
                    ]}
                    className="flex-1"
                  >
                    <AutoComplete
                      placeholder="Chiave"
                      options={metadata.data}
                      filterOption={(inputValue, option: any) =>
                        option!.value.toUpperCase().includes(inputValue.toUpperCase())
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'value']}
                    rules={[{ required: true, message: 'Inserisci un valore' }]}
                    className="flex-1"
                  >
                    <Input.TextArea rows={1} placeholder="Valore" />
                  </Form.Item>
                  <Button
                    type="text"
                    icon={<IconTrash size={16} />}
                    className="text-red-600 mt-[2px]"
                    onClick={() => remove(name)}
                  />
                </div>
              ))}
            </>
          )}
        </Form.List>
      </Form>
    </CModal>
  );
};

export default CustomerMetadataModal;
